import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch } from 'react-router';
import SimpleButton from '../../components/SimpleButton';
import { IApplicationState } from '../../store';
import { CustomLink, ItemsList } from './styles';
import CustomTable from '../../components/CustomTable';
import { ISweepRoomTime } from '../../store/sweeps/types';
import RoomItemItem from '../../components/RoomItemItem';
import history from '../../providers/history';
import { startEditingSweepRoomItem, sweepRoomDraftUpdate, sweepRoomRemoveRequest, roomExportPhotosRequest, roomExportPhotosFlirRequest, roomFloorPlanSaveRequest, roomFrequencyFileSaveRequest, roomFrequencyFileDeleteRequest, roomExportVideosRequest, roomFrequencyAnalysisSaveRequest, startEditingRoomRegister, roomWorkRegisterRemoveRequest } from '../../store/sweeps/slice';
import ButtonGroup from '../../components/ButtonGroup';
import Spacer from '../../components/Spacer';
import { systemAddDialog } from '../../store/system/slice';
import ErrorMessage from '../../components/ErrorMessage';
import Message from '../../components/Message';
import { roomFlirPhotoSaveRequest, roomPhotoSaveRequest, roomPhotosFlirRequest, roomPhotosRequest } from '../../store/photos/slice';
import PhotoGallery from '../../components/PhotoGallery';
import FileUploader from '../../components/FileUploader';
import FlirPhotoGallery from '../../components/FlirPhotoGallery';
import { useTranslation } from 'react-i18next';
import { BASE_URL } from '../../providers/api';
import { IUploadedInfo } from '../../store/photos/types';
import { roomVideoSaveRequest, roomVideosRequest } from '../../store/videos/slices';
import VideoGallery from '../../components/VideoGallery';
;

const RoomDetails: React.FC = (props) => {
    const route = useMatch('/room/:idSweep/:idRoom');

    const { t } = useTranslation();

    const idSweep = route?.params.idSweep;

    const idRoom = route?.params.idRoom;

    const sweep = useSelector((state: IApplicationState) => state.sweeps.data.find(sweep => sweep.id === idSweep))

    const photos = useSelector((state: IApplicationState) => state.photos);

    const videos = useSelector((state: IApplicationState) => state.videos);

    const room = sweep?.rooms.find(room => room.id === idRoom);

    const [uploadingPhotos, setUploadingPhotos] = useState(false);

    const [uploadingFlirPhotos, setUploadingFlirPhotos] = useState(false);

    const [uploadingVideos, setUploadingVideos] = useState(false);

    const [uploadingFloorPlan, setUploadingFloorPlan] = useState(false);

    const [uploadingFrequencyFile, setUploadingFrequencyFile] = useState(false);

    const [uploadingFrequencyAnalysis, setUploadingFrequencyAnalysis] = useState(false);

    const [showFlirPhoto, setShowFlirPhoto] = useState(true);

    const dispatch = useDispatch();

    const [flirUpload, setFlirUpload] = useState<{
        normal?: IUploadedInfo,
        flir?: IUploadedInfo,
        roomId?: string,
    }>({
        normal: undefined,
        flir: undefined,
        roomId: undefined
    })

    const flirUploadCallback = useCallback(
        (data: IUploadedInfo, type: 'normal' | 'flir', roomId) => {

            if ((type === 'normal' && flirUpload.flir !== undefined) ||
                (type === 'flir' && flirUpload.normal !== undefined)) {

                if (roomId !== undefined) {
                    dispatch(roomFlirPhotoSaveRequest({
                        uploadNormalId: flirUpload.normal !== undefined ? flirUpload.normal.uploadId : data.uploadId,
                        uploadFlirId: flirUpload.flir !== undefined ? flirUpload.flir.uploadId : data.uploadId,
                        path: flirUpload.normal === undefined ? data.path : flirUpload.normal.path,
                        roomId: roomId,
                        flirPath: flirUpload.flir === undefined ? data.path : flirUpload.flir.path,
                    }))
                }
            }

            else setFlirUpload({
                ...flirUpload,
                [type]: data, roomId
            })
        },
        [flirUpload, dispatch],
    )

    useEffect(() => {
        if (room?.id) {
            dispatch(roomPhotosRequest({
                roomId: room.id
            }))

            dispatch(roomPhotosFlirRequest({
                roomId: room.id
            }))

            dispatch(roomVideosRequest({
                roomId: room.id
            }))
        }
    }, [dispatch, room?.id])

    const calculateTotal = (time: ISweepRoomTime): string => {
        const totalTime = (time.endingAt ? time.endingAt.getTime() : new Date().getTime()) - (time.beginingAt ? time.beginingAt.getTime() : new Date().getTime())
        const hours = Math.floor(totalTime / (1000 * 60 * 60));
        const minutes = Math.floor(totalTime / (1000 * 60)) % 60;
        const seconds = Math.floor(totalTime / 1000) % 60;

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    if (!room) return <h1>{t("pages.rooms.not_found")}</h1>;

    const roomPhotos = photos.roomPhotos[room.id];

    const roomVideos = videos.roomVideos[room.id];

    const roomFlirPhotos = photos.roomFlirPhotos[room.id];

    if (room.isDeleting) return <h1>{t("pages.rooms.deleting")}</h1>;

    return (<>
        {room.error !== undefined && <ErrorMessage>{room.error.message || "Unexpected error"}</ErrorMessage>}

        <h1>{room.name}</h1>

        <p><strong>{t("pages.rooms.width")}:</strong> {room.width ? `${room.width}${room.metricUnit || 'm'}` : <em>{t("common.undefined")}</em>}</p>
        <Spacer size={5} />
        <p><strong>{t("pages.rooms.length")}:</strong> {room.length ? `${room.length}${room.metricUnit || 'm'}` : <em>{t("common.undefined")}</em>}</p>
        <Spacer size={5} />
        <p><strong>{t("pages.rooms.area")}:</strong> {room.width && room.length ? `${(room.width * room.length).toFixed(2)}${room.metricUnit || 'm'}²` : <em>{t("common.undefined")}</em>}</p>

        <Spacer size={10} />

        <ButtonGroup>
            <SimpleButton size={'small'} prependIcon='pencil' color='primary' onClick={() => {
                dispatch(sweepRoomDraftUpdate(room));

                history.push(`${room.id}/form`);
            }}>{t("common.buttons.edit")}</SimpleButton>
            <SimpleButton size={'small'} prependIcon='trash' color='danger' onClick={() => {
                dispatch(systemAddDialog({
                    title: t("pages.rooms.dialogs.remove_room"),
                    message: t("pages.rooms.dialogs.remove_room_message"),
                    actions: [{
                        text: t("pages.rooms.buttons.remove_room_confirm"),
                        action: () => {
                            dispatch(sweepRoomRemoveRequest(room.id));
                        },
                        color: 'danger',
                        prependIcon: 'trash'
                    },
                    {
                        text: t("pages.rooms.buttons.remove_room_cancel"),
                        action: () => { },
                        color: 'none'
                    }]
                }));
            }}>{t("common.buttons.delete")}</SimpleButton>
        </ButtonGroup>

        <h1>{t("pages.rooms.items")}</h1>
        <ItemsList>
            {room.items.map(item => <CustomLink to={`/item/${idSweep}/${idRoom}/${item.id}`} key={`item-id-${item.id}`}><RoomItemItem item={item} /></CustomLink>)}
        </ItemsList>

        {room.items.length === 0 && <p>{t("pages.rooms.zero_items")}</p>}

        <Spacer size={20} />

        <ButtonGroup>
            <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => {
                dispatch(startEditingSweepRoomItem({
                    id: "",
                    name: "",
                    roomId: room.id,
                    sweepId: room.sweepId
                }))

                history.push(`/item/${idSweep}/${idRoom}/form`);
            }}>{t("pages.rooms.buttons.add_item")}</SimpleButton>
        </ButtonGroup>

        <h1>{t("pages.rooms.photos")}</h1>

        {roomPhotos && roomPhotos.data.length > 0 && <PhotoGallery type='room' parentId={room.id} sweepId={room.sweepId} photos={roomPhotos.data} />}

        {(!roomPhotos || roomPhotos.data.length === 0) && !roomPhotos?.isLoading && <p>{t("pages.rooms.zero_photos")}</p>}
        <Spacer size={20} />

        {roomPhotos?.error && <>
            <ErrorMessage>{roomPhotos.error.message}</ErrorMessage>
            <Spacer size={20} />
        </>}

        <ButtonGroup>
            {!uploadingPhotos && <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => { setUploadingPhotos(true) }}>{t("pages.rooms.buttons.show_upload")}</SimpleButton>}
            {uploadingPhotos && <SimpleButton size={'small'} prependIcon='minus' color='primary' onClick={() => { setUploadingPhotos(false) }}>{t("pages.rooms.buttons.hide_upload")}</SimpleButton>}

            <SimpleButton disabled={room.isExportingPhotos} size={'small'} prependIcon={'download'} color='info' loading={room.isExportingPhotos} onClick={() => { if (room.id) dispatch(roomExportPhotosRequest(room.id)) }}>{room.isExportingPhotos ? t("pages.sweeps.buttons.exporting") : t("pages.sweeps.buttons.export")}</SimpleButton>

            <SimpleButton size={'small'} loading={roomPhotos?.isLoading} prependIcon='refresh' color='warning' onClick={() => {
                dispatch(roomPhotosRequest({
                    roomId: room.id
                }))
            }}>{roomPhotos?.isLoading ? t("pages.rooms.loading_photos") : ''}
            </SimpleButton>
        </ButtonGroup>

        <Spacer size={20} />

        {uploadingPhotos && <FileUploader entityId={room.id} type='image' callback={(uploaded) => {

            dispatch(roomPhotoSaveRequest({
                uploadId: uploaded.uploadId,
                roomId: room.id,
                path: uploaded.path
            }))
        }} />}

        {/* **** PHOTOS FLIR *** */}

        <h1>{t("pages.rooms.flir")}</h1>

        {!showFlirPhoto && (roomFlirPhotos && roomFlirPhotos.data.length > 0 && <FlirPhotoGallery photos={roomFlirPhotos.data} parentId={room.id} type={"room"} typePhoto={'normal'} />)}
        {showFlirPhoto && (roomFlirPhotos && roomFlirPhotos.data.length > 0 && <FlirPhotoGallery photos={roomFlirPhotos.data} parentId={room.id} type={"room"} typePhoto={'flir'} />)}

        {(!roomFlirPhotos || roomFlirPhotos.data.length === 0) && !roomFlirPhotos?.isLoading && <p>{t("pages.rooms.zero_flir")}</p>}

        <Spacer size={20} />

        <ButtonGroup>

            {!uploadingFlirPhotos && <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => { setUploadingFlirPhotos(true) }}>{t("pages.rooms.buttons.add_flir")}</SimpleButton>}
            {uploadingFlirPhotos && <SimpleButton size={'small'} prependIcon='minus' color='primary' onClick={() => { setUploadingFlirPhotos(false) }}>{t("pages.rooms.buttons.hide_flir")}</SimpleButton>}

            {!showFlirPhoto && <SimpleButton size={'small'} prependIcon='photo' color='secondary' onClick={() => { setShowFlirPhoto(true) }}>{t("pages.items.buttons.show_photo_flir")}</SimpleButton>}
            {showFlirPhoto && <SimpleButton size={'small'} prependIcon='photo' color='secondary' onClick={() => { setShowFlirPhoto(false) }}>{t("pages.items.buttons.show_photo_normal")}</SimpleButton>}

            <SimpleButton disabled={room.isExportingFlir} size={'small'} prependIcon={'download'} color='info' loading={room.isExportingFlir} onClick={() => { if (room.id) dispatch(roomExportPhotosFlirRequest(room.id)) }}>{room.isExportingFlir ? t("pages.sweeps.buttons.exporting") : t("pages.sweeps.buttons.export")}</SimpleButton>

            <SimpleButton loading={roomFlirPhotos?.isLoading} size={'small'} prependIcon='refresh' color='warning' onClick={() => {
                dispatch(roomPhotosFlirRequest({
                    roomId: room.id
                }))
            }}>{roomFlirPhotos?.isLoading ? t("pages.rooms.loading_flir") : ''}</SimpleButton>
        </ButtonGroup>

        <Spacer size={15} />

        {uploadingFlirPhotos && <strong>{t("pages.items.uploading_normal")}</strong>}

        <Spacer size={10} />

        {uploadingFlirPhotos && <FileUploader type={'image'} entityId={room.id} callback={(uploaded) => {
            flirUploadCallback(uploaded, 'normal', room.id);
        }} />}

        <Spacer size={10} />

        {uploadingFlirPhotos && <strong>{t("pages.items.uploading_flir")}</strong>}

        <Spacer size={10} />

        {uploadingFlirPhotos && <FileUploader type={'imageFlir'} entityId={room.id} callback={(uploaded) => {
            flirUploadCallback(uploaded, 'flir', room.id);
        }} />}

        <h1>{t("pages.rooms.floor_plan")}</h1>
        {room.isDeletingFloorPlan && <p>Deletando...</p>}
        {(room.floorPlan !== undefined && room.floorPlan !== null) && <PhotoGallery type='floorplan' parentId={room.id} sweepId={room.sweepId} photos={[{
            fileName: '',
            id: room.id,
            link: `${BASE_URL}download?type=floorplan&id=${room.id}&path=${room.floorPlan}`,
        }]} />}

        {!room.floorPlan && <p>{t("pages.rooms.zero_floor_plan")}</p>}
        <Spacer size={20} />
        <ButtonGroup>
            {!uploadingFloorPlan && <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => { setUploadingFloorPlan(true) }}>{t("pages.rooms.buttons.show_upload")}</SimpleButton>}
            {uploadingFloorPlan && <SimpleButton size={'small'} prependIcon='minus' color='primary' onClick={() => { setUploadingFloorPlan(false) }}>{t("pages.rooms.buttons.hide_upload")}</SimpleButton>}
        </ButtonGroup>

        <Spacer size={20} />

        {uploadingFloorPlan && <FileUploader entityId={room.id} type='image' callback={(uploaded) => {

            dispatch(roomFloorPlanSaveRequest({
                uploadId: uploaded.uploadId,
                sweepId: sweep?.id || "",
                roomId: room.id,
                floorPlan: uploaded.path
            }))
        }} />}

        {/* **** VIDEOS *** */}

        <h1>{t("pages.rooms.videos")}</h1>

        {roomVideos && roomVideos.data.length > 0 && <VideoGallery parentId={room.id} videos={roomVideos.data} />}

        {(!roomVideos || roomVideos.data.length === 0) && !roomVideos?.isLoading && <p>{t("pages.rooms.zero_videos")}</p>}


        <Spacer size={20} />

        {roomVideos?.error && <>
            <ErrorMessage>{roomVideos.error.message}</ErrorMessage>
            <Spacer size={20} />
        </>}

        <ButtonGroup>
            {!uploadingVideos && <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => { setUploadingVideos(true) }}>{t("pages.rooms.buttons.show_upload")}</SimpleButton>}
            {uploadingVideos && <SimpleButton size={'small'} prependIcon='minus' color='primary' onClick={() => { setUploadingVideos(false) }}>{t("pages.rooms.buttons.hide_upload")}</SimpleButton>}

            <SimpleButton disabled={room.isExportingVideos} size={'small'} prependIcon={'download'} color='info' loading={room.isExportingVideos} onClick={() => { if (room.id) dispatch(roomExportVideosRequest(room.id)) }}>{room.isExportingVideos ? t("pages.sweeps.buttons.exporting") : t("pages.sweeps.buttons.export")}</SimpleButton>

            <SimpleButton size={'small'} loading={roomVideos?.isLoading} prependIcon='refresh' color='warning' onClick={() => {
                dispatch(roomVideosRequest({
                    roomId: room.id
                }))
            }}>{roomVideos?.isLoading ? t("pages.rooms.loading_photos") : ''}
            </SimpleButton>
        </ButtonGroup>

        <Spacer size={20} />

        {uploadingVideos && <FileUploader entityId={room.id} type='video' callback={(uploaded) => {

            dispatch(roomVideoSaveRequest({
                uploadId: uploaded.uploadId,
                roomId: room.id,
                path: uploaded.path
            }))
        }} />}

        {/* **** FREUQENCY FILES *** */}

        <h1>{t("pages.rooms.frequency_file")}</h1>
        {room.isDeletingFrequencyFile && <p>Deletando...</p>}

        {!room.frequencyFile && <p>{t("pages.rooms.zero_frequency_file")}</p>}
        {room.frequencyFile && <Message type={"success"}>{t("pages.rooms.upload_frequency_file")}</Message>}
        <Spacer size={20} />

        <ButtonGroup>
            {!uploadingFrequencyFile && <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => { setUploadingFrequencyFile(true) }}>{t("pages.rooms.buttons.show_upload")}</SimpleButton>}
            {uploadingFrequencyFile && <SimpleButton size={'small'} prependIcon='minus' color='primary' onClick={() => { setUploadingFrequencyFile(false) }}>{t("pages.rooms.buttons.hide_upload")}</SimpleButton>}
            <SimpleButton size={'small'} prependIcon='trash' color='danger' onClick={() => {
                dispatch(systemAddDialog({
                    title: t("pages.rooms.dialogs.remove_file"),
                    message: t("pages.rooms.dialogs.remove_file_message"),
                    actions: [{
                        text: t("pages.rooms.buttons.remove_file_confirm"),
                        action: () => {
                            dispatch(roomFrequencyFileDeleteRequest({
                                roomId: room.id,
                                error: undefined,
                                sweepId: room.sweepId
                            }));
                        },
                        color: 'danger',
                        prependIcon: 'trash'
                    },
                    {
                        text: t("pages.rooms.buttons.remove_file_cancel"),
                        action: () => { },
                        color: 'none'
                    }]
                }));
            }}>{t("common.buttons.delete")}</SimpleButton>
        </ButtonGroup>


        <Spacer size={20} />

        {uploadingFrequencyFile && <FileUploader entityId={room.id} type='image' callback={(uploaded) => {

            dispatch(roomFrequencyFileSaveRequest({
                uploadId: uploaded.uploadId,
                sweepId: sweep?.id || "",
                roomId: room.id,
                frequencyFile: uploaded.path
            }))
        }} />}

        {/* **** FREQUENCY ANALYSIS */}

        <h1>{t("pages.rooms.frequency_analysis")}</h1>
        {room.isDeletingFrequencyAnalysis && <p>Deletando...</p>}

        {!room.frequencyAnalysis && <p>{t("pages.rooms.zero_frequency_analysis")}</p>}
        {(room.frequencyAnalysis !== undefined && room.frequencyAnalysis !== null) && <PhotoGallery type='frequencyAnalysis' parentId={room.id} sweepId={room.sweepId} photos={[{
            fileName: '',
            id: room.id,
            link: `${BASE_URL}download?type=frequencyanalysis&id=${room.id}&path=${room.frequencyAnalysis}`,
        }]} />}

        <Spacer size={20} />

        <ButtonGroup>
            {!uploadingFrequencyAnalysis && <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => { setUploadingFrequencyAnalysis(true) }}>{t("pages.rooms.buttons.show_upload")}</SimpleButton>}
            {uploadingFrequencyAnalysis && <SimpleButton size={'small'} prependIcon='minus' color='primary' onClick={() => { setUploadingFrequencyAnalysis(false) }}>{t("pages.rooms.buttons.hide_upload")}</SimpleButton>}
        </ButtonGroup>


        <Spacer size={20} />

        {uploadingFrequencyAnalysis && <FileUploader entityId={room.id} type='image' callback={(uploaded) => {

            dispatch(roomFrequencyAnalysisSaveRequest({
                uploadId: uploaded.uploadId,
                sweepId: sweep?.id || "",
                roomId: room.id,
                frequencyAnalysis: uploaded.path
            }))
        }} />}

        {/* **** REGISTER WORK *** */}

        <h1>{t("pages.rooms.work_register")}</h1>

        {room.times.length === 0 && <p>{t("pages.rooms.zero_work_register")}</p>}

        {room.times.length > 0 && <CustomTable>
            <thead>
                <tr>
                    <th style={{ minWidth: 160 }}>Begining</th>
                    <th style={{ minWidth: 160 }}>Ending</th>
                    <th style={{ minWidth: 90 }}>Total</th>
                    <th style={{ minWidth: 250 }}>Observations</th>
                    <th style={{ minWidth: 150, width: 150 }}>Actions</th>
                </tr>
            </thead>
            <tbody>
                {room.times.map(time => <tr key={`time-id-${time.id}`}>
                    <td>{time.beginingAt && time.beginingAt.toLocaleString()}</td>
                    <td>{time.endingAt ? time.endingAt.toLocaleString() : <em>{t("common.undefined")}</em>}</td>
                    <td>{calculateTotal(time)}</td>
                    <td>{time.observation ? time.observation : <em>{t("common.undefined")}</em>}</td>
                    <td><SimpleButton size={'small'} prependIcon='trash' color='danger' onClick={() => {

                        return dispatch(systemAddDialog({
                            title: t("pages.sweeps.dialogs.remove_work_register"),
                            message: t("pages.sweeps.dialogs.remove_work_register_message"),
                            actions: [{
                                text: t("pages.sweeps.buttons.remove_work_register_confirm"),
                                action: () => {
                                    time.id && dispatch(roomWorkRegisterRemoveRequest(time.id))
                                },
                                color: 'danger',
                                prependIcon: 'trash'
                            },
                            {
                                text: t("pages.sweeps.buttons.remove_work_register_cancel"),
                                action: () => { },
                                color: 'none'
                            }]
                        }));

                    }}>{t("common.buttons.delete")}</SimpleButton></td>
                </tr>)}
            </tbody>
        </CustomTable>}
        <Spacer size={20} />
        <ButtonGroup>
            <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => {
                dispatch(startEditingRoomRegister({
                    sweepId: sweep?.id || '',
                    roomId: room.id || '',
                }))
                history.push(`/room-work-register/${room.sweepId}/${room.id}/form`)
            }}>{t("pages.rooms.buttons.add_work_register")}</SimpleButton>
        </ButtonGroup>
    </>);
}

export default RoomDetails;